import { useState, useEffect, useContext, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Paper, useTheme } from '@mui/material';

import ContactDrawer from '../ContactDrawer';
import MessageInput from '../MessageInput';
import TicketHeader from '../TicketHeader';
import TicketInfo from '../TicketInfo';
import TicketActionButtons from '../TicketActionButtonsCustom';
import MessagesList from '../MessagesList';
import api from '../../services/api';

import toastError from '../../toasts/toastError';
import { AuthContext } from '../../context/Auth/AuthContext';
import { TicketsContext } from '../../context/Tickets/TicketsContext';
import { ReplyMessageProvider } from '../../context/ReplyingMessage/ReplyingMessageContext';
import { ForwardMessageProvider } from '../../context/ForwarMessage/ForwardMessageContext';
import { EditMessageProvider } from '../../context/EditingMessage/EditingMessageContext';
import { TagsContainer } from '../TagsContainer';

import { isNil } from 'lodash';
import { getStyled } from './styles';

const Ticket = () => {
  const { ticketId } = useParams();
  const navigate = useNavigate();
  const theme = useTheme();
  const classes = getStyled(theme);

  const { user, socket } = useContext(AuthContext);
  const { setTabOpen } = useContext(TicketsContext);
  const { companyId } = user;

  const [drawerOpen, setDrawerOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [contact, setContact] = useState({});
  const [ticket, setTicket] = useState({});
  const [dragDropFiles, setDragDropFiles] = useState([]);

  const isMounted = useRef(true);
  useEffect(() => {
    isMounted.current = true;
    return () => {
      isMounted.current = false;
    };
  }, []);

  useEffect(() => {
    if (isNil(ticketId) || ticketId === 'undefined') return;

    setLoading(true);

    const delayDebounceFn = setTimeout(async () => {
      try {
        const { data } = await api.get(`/tickets/u/${ticketId}`);
        if (!isMounted.current) return;
        setContact(data.contact);
        setTicket(data);
        if (['pending', 'open', 'group'].includes(data.status)) {
          setTabOpen(data.status);
        }
      } catch (err) {
        if (!isMounted.current) return;
        navigate('/tickets');
        toastError(err);
      } finally {
        if (isMounted.current) setLoading(false);
      }
    }, 500);

    return () => clearTimeout(delayDebounceFn);
  }, [ticketId, navigate, setTabOpen]);

  useEffect(() => {
    if (!ticket?.id || ticket.uuid !== ticketId || ticketId === 'undefined')
      return;

    if (!socket || typeof socket.on !== 'function') {
      console.warn('Socket não disponível em Ticket');
      return;
    }

    const onConnectTicket = () => {
      if (socket && typeof socket.emit === 'function') {
        socket.emit('joinChatBox', `${ticket.id}`);
      }
    };

    onConnectTicket();

    const onCompanyTicket = (data) => {
      if (data.action === 'update' && data.ticket.id === ticket?.id) {
        if (isMounted.current) setTicket(data.ticket);
      }
      if (data.action === 'delete' && data.ticketId === ticket?.id) {
        navigate('/tickets');
      }
    };

    const onCompanyContactTicket = (data) => {
      if (data.action === 'update') {
        setContact((prev) =>
          prev.id === data.contact?.id ? { ...prev, ...data.contact } : prev,
        );
      }
    };

    socket.on('connect', onConnectTicket);
    socket.on(`company-${companyId}-ticket`, onCompanyTicket);
    socket.on(`company-${companyId}-contact`, onCompanyContactTicket);

    return () => {
      if (socket && typeof socket.emit === 'function') {
        socket.emit('joinChatBoxLeave', `${ticket.id}`);
      }
      if (socket && typeof socket.off === 'function') {
        socket.off('connect', onConnectTicket);
        socket.off(`company-${companyId}-ticket`, onCompanyTicket);
        socket.off(`company-${companyId}-contact`, onCompanyContactTicket);
      }
    };
  }, [ticketId, ticket?.id, navigate, socket, companyId]); //eslint-disable-line

  const handleDrawerOpen = useCallback(() => setDrawerOpen(true), []);
  const handleDrawerClose = useCallback(() => setDrawerOpen(false), []);

  const renderMessagesList = () => (
    <>
      <MessagesList
        isGroup={ticket.isGroup}
        onDrop={setDragDropFiles}
        whatsappId={ticket.whatsappId}
        queueId={ticket.queueId}
        channel={ticket.channel}
      />
      <MessageInput
        ticket={ticket}
        ticketId={ticket.id}
        ticketStatus={ticket.status}
        ticketChannel={ticket.channel}
        droppedFiles={dragDropFiles}
        contactId={contact.id}
      />
    </>
  );

  return (
    <div style={classes.root} id='drawer-container'>
      <Paper variant='outlined' elevation={0} sx={classes.mainWrapper}>
        <TicketHeader loading={loading}>
          <div style={classes.ticketHeader}>
            {ticket.contact !== undefined && (
              <TicketInfo
                contact={contact}
                ticket={ticket}
                onClick={handleDrawerOpen}
              />
            )}
            <TicketActionButtons
              key={`${ticket.id}-${ticket.status}`}
              ticket={ticket}
            />
          </div>
        </TicketHeader>

        <Paper>
          <TagsContainer contact={contact} />
        </Paper>

        <ReplyMessageProvider>
          <ForwardMessageProvider>
            <EditMessageProvider>{renderMessagesList()}</EditMessageProvider>
          </ForwardMessageProvider>
        </ReplyMessageProvider>
      </Paper>

      <ContactDrawer
        open={drawerOpen}
        handleDrawerClose={handleDrawerClose}
        contact={contact}
        loading={loading}
        ticket={ticket}
      />
    </div>
  );
};

export default Ticket;
